/**
 * Sterling Forever — runtime accessibility cleanup for collection pages.
 *
 * Patches DOM after render to resolve WAVE errors that originate in Horizon
 * core snippets we can't freely restructure (swatch/image filters, quickview
 * dialog, mega menu, cart drawer):
 *
 *   - Missing / empty alt on <img> (derives from nearest labeled ancestor).
 *   - Multiple labels on form inputs (keeps <label for> or aria-label, not both).
 *   - Empty <label> elements (pulls text from nested inputs / fieldsets).
 *   - Empty <a> elements (derives from href slug or child image alt).
 *   - Broken aria-controls / aria-labelledby / aria-describedby references
 *     (removes the attribute when the target ID is absent from the DOM).
 *
 * Runs on DOMContentLoaded, re-runs on shopify:section:load and after facet
 * filter updates (which inject fresh DOM via the Section Rendering API).
 */
(() => {
  'use strict';

  // Guard against double initialization (loaded globally by theme.liquid AND
  // by individual section files during earlier iterations).
  if (window.__sfA11yFixesLoaded) return;
  window.__sfA11yFixesLoaded = true;

  const slugToLabel = (slug) => {
    if (!slug) return '';
    return slug
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean)
      .pop()
      ?.replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()) || '';
  };

  const hasAccessibleName = (el) => {
    if (!el) return false;
    if (el.getAttribute('aria-label')?.trim()) return true;
    if (el.getAttribute('aria-labelledby')?.trim()) return true;
    if (el.textContent.trim()) return true;
    return false;
  };

  const deriveAltFromContext = (img) => {
    const link = img.closest('a[href]');
    const fieldset = img.closest('fieldset');
    const label = img.closest('label');
    const container = img.closest('[data-product-title], [data-collection-title]');

    // Prefer data-* hints from our templates.
    const dataAlt =
      container?.dataset.productTitle ||
      container?.dataset.collectionTitle ||
      img.dataset.alt;
    if (dataAlt) return dataAlt;

    // Associated fieldset/label aria-label (common in filter UI).
    const fsLabel = fieldset?.getAttribute('aria-label');
    if (fsLabel) return fsLabel;
    const lblLabel = label?.getAttribute('aria-label');
    if (lblLabel) return lblLabel;

    // Nearby text: title span inside the same link.
    if (link) {
      const linkLabel = link.getAttribute('aria-label');
      if (linkLabel) return linkLabel;
      const textNode = Array.from(link.querySelectorAll('span, p, h1, h2, h3, h4'))
        .map((n) => n.textContent.trim())
        .find(Boolean);
      if (textNode) return textNode;
      const slug = slugToLabel(link.getAttribute('href'));
      if (slug) return slug;
    }

    return '';
  };

  /** 1. Every <img> needs an alt attribute (empty string is valid for decorative). */
  const fixImageAlts = (root) => {
    root.querySelectorAll('img:not([alt])').forEach((img) => {
      img.setAttribute('alt', deriveAltFromContext(img));
    });
    // Images with empty alt inside a link where the link has no other
    // accessible text = WAVE "linked image missing alt".
    root.querySelectorAll('a img[alt=""]').forEach((img) => {
      const link = img.closest('a');
      if (!link) return;
      if (hasAccessibleName(link)) return;
      // Link has no other text; derive alt for the image.
      const derived = deriveAltFromContext(img);
      if (derived) img.setAttribute('alt', derived);
    });
  };

  /** 2. Input with multiple label sources = WAVE error. Keep exactly one.
   *  WAVE flags BOTH visible and display:none labels, so we don't gate on
   *  visibility — we just pick the best single label and demote the rest.
   */
  const fixMultipleLabels = (root) => {
    // Swatch pattern: outer <label> wraps input + inner <label for>. Keep the
    // outer (with visually-hidden text) as the single label source. Demote
    // the inner by stripping its `for` attribute.
    root
      .querySelectorAll('label.variant-option__button-label')
      .forEach((outer) => {
        const input = outer.querySelector('input');
        if (!input) return;

        // Find the inner label (if any).
        const inner = input.id
          ? outer.querySelector(`label[for="${CSS.escape(input.id)}"]`)
          : null;

        // Ensure the outer label carries accessible text (visually-hidden).
        // Uses inner label text, aria-label, or input value as the source.
        const hasOuterText = Array.from(outer.childNodes).some(
          (n) =>
            (n.nodeType === 3 && n.textContent.trim()) ||
            (n.nodeType === 1 &&
              n !== inner &&
              !n.contains(input) &&
              n.textContent.trim())
        );
        if (!hasOuterText) {
          const labelText =
            (inner && inner.textContent.trim()) ||
            input.getAttribute('aria-label') ||
            input.value ||
            '';
          if (labelText) {
            const span = document.createElement('span');
            span.className = 'visually-hidden';
            span.textContent = labelText;
            outer.insertBefore(span, outer.firstChild);
          }
        }

        input.removeAttribute('aria-label');
        if (inner && inner !== outer) inner.removeAttribute('for');
      });

    // General case: any <input> with aria-label AND a <label for> elsewhere.
    // Prefer the <label for> association, drop aria-label.
    root.querySelectorAll('input[aria-label][id]').forEach((input) => {
      const id = input.id;
      if (!id) return;
      const explicit = root.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (explicit) input.removeAttribute('aria-label');
    });

    // Sorting / listbox pattern: <label for="X"> wraps <input id="X">. That
    // double-labels the input (explicit `for` + implicit wrapping). Drop the
    // `for` — the nesting alone provides the label association and WAVE
    // stops counting two separate label sources.
    root.querySelectorAll('label[for]').forEach((label) => {
      const forId = label.getAttribute('for');
      if (!forId) return;
      const target = document.getElementById(forId);
      if (target && label.contains(target) && target !== label) {
        label.removeAttribute('for');
      }
    });

    // Same for select / textarea, in case Horizon adds any.
    root.querySelectorAll('select[aria-label][id], textarea[aria-label][id]').forEach((el) => {
      const id = el.id;
      if (!id) return;
      const explicit = root.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (explicit) el.removeAttribute('aria-label');
    });
  };

  /** 3. <label> must have accessible text. */
  const fixEmptyLabels = (root) => {
    root.querySelectorAll('label').forEach((label) => {
      if (hasAccessibleName(label)) return;
      // Derive from associated input value / option name / fieldset label.
      const forId = label.getAttribute('for');
      const input = forId
        ? root.getElementById(forId)
        : label.querySelector('input, select, textarea');
      // A wrapper <label> around an already-named control needs no name of its own, and
      // aria-label on <label> is prohibited: <label> maps to no ARIA role (WCAG 4.1.2).
      if (input && hasAccessibleName(input)) return;

      const fieldset = label.closest('fieldset');
      const derived =
        input?.dataset.label ||
        input?.value ||
        fieldset?.getAttribute('aria-label') ||
        '';
      if (derived && input) {
        // Name the control, never the <label> wrapper.
        input.setAttribute('aria-label', derived);
      } else if (derived) {
        label.setAttribute('role', 'presentation');
      } else {
        // Label provides no benefit — neutralize so WAVE stops counting it.
        label.setAttribute('role', 'presentation');
      }
    });
  };

  /** 4. <a> needs an accessible name. */
  const fixEmptyLinks = (root) => {
    root.querySelectorAll('a').forEach((link) => {
      if (hasAccessibleName(link)) return;
      // A labeled child image satisfies WAVE.
      if (link.querySelector('img[alt]:not([alt=""])')) return;
      // SVG with <title> also counts as an accessible name for the link.
      if (link.querySelector('svg title')) return;

      let derived =
        link.getAttribute('title') ||
        link.querySelector('img')?.getAttribute('alt') ||
        link.querySelector('[data-label]')?.getAttribute('data-label') ||
        slugToLabel(link.getAttribute('href'));

      // Give any unnamed <img> inside the link an alt from the same source.
      if (derived) {
        const blankImg = link.querySelector('img:not([alt]), img[alt=""]');
        if (blankImg) blankImg.setAttribute('alt', derived);
        link.setAttribute('aria-label', derived);
      } else {
        // Last-resort: mark decorative/presentational so WAVE stops flagging.
        // Only do this when the link is NOT a real navigation target.
        const href = link.getAttribute('href');
        if (!href || href === '#' || href === '') {
          link.setAttribute('role', 'presentation');
          link.setAttribute('aria-hidden', 'true');
          link.setAttribute('tabindex', '-1');
        } else {
          link.setAttribute('aria-label', 'Link');
        }
      }
    });
  };

  /** 5. Strip aria-* attributes pointing at IDs that aren't in the DOM. */
  const fixBrokenAriaRefs = (root) => {
    const doc = root.ownerDocument || document;
    ['aria-controls', 'aria-labelledby', 'aria-describedby'].forEach((attr) => {
      root.querySelectorAll(`[${attr}]`).forEach((el) => {
        const raw = el.getAttribute(attr);
        if (!raw) return;
        const tokens = raw.split(/\s+/).filter(Boolean);
        const valid = tokens.filter((id) => doc.getElementById(id));
        if (valid.length === 0) {
          el.removeAttribute(attr);
        } else if (valid.length !== tokens.length) {
          el.setAttribute(attr, valid.join(' '));
        }
      });
    });
  };

  /** 6. Suffix duplicate IDs so labels/aria-refs resolve unambiguously.
   *  Horizon renders the sorting + filter snippets multiple times (desktop,
   *  mobile, overflow, drawer) producing duplicate element IDs. WAVE flags
   *  those as "Duplicate ID" errors AND can report them as extra labels.
   */
  const dedupeDuplicateIds = (root) => {
    const byId = new Map();
    root.querySelectorAll('[id]').forEach((el) => {
      const id = el.id;
      if (!id) return;
      const list = byId.get(id) || [];
      list.push(el);
      byId.set(id, list);
    });
    byId.forEach((list, id) => {
      if (list.length < 2) return;
      // Keep the first occurrence untouched; rename the rest and update any
      // in-DOM references (label[for], aria-controls, aria-labelledby, etc).
      list.slice(1).forEach((el, i) => {
        const newId = `${id}-dup${i + 1}`;
        // Find labels referencing the old id that are DOM-adjacent to this el
        // (same parent tree) and update them before renaming the element.
        const scope = el.closest('form, fieldset, .facets, .sorting-filter, details') || document;
        scope.querySelectorAll(`label[for="${CSS.escape(id)}"]`).forEach((lbl) => {
          if (lbl.contains(el) || lbl.parentElement?.contains(el)) {
            lbl.setAttribute('for', newId);
          }
        });
        el.id = newId;
      });
    });
  };

  const runAll = (root = document) => {
    try {
      // Dedupe IDs first so label[for] / aria-ref fixes see unique targets.
      dedupeDuplicateIds(root);
      fixImageAlts(root);
      fixMultipleLabels(root);
      fixEmptyLabels(root);
      fixEmptyLinks(root);
      fixBrokenAriaRefs(root);
    } catch (_) {
      /* swallow — accessibility patch must never break the page */
    }
  };

  const init = () => {
    runAll(document);

    // Deferred reruns catch widgets that hydrate after DOMContentLoaded
    // (quickview, cart drawer remotes, deferred videos).
    [500, 2000, 5000].forEach((delay) =>
      setTimeout(() => runAll(document), delay)
    );

    // Theme editor reloads a section: re-patch just that subtree.
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof HTMLElement) runAll(event.target);
    });

    // Facet filter updates inject fresh DOM via the Section Rendering API.
    // Horizon fires "filter:rendered" on the results root; also fall back to
    // a MutationObserver on known containers.
    document.addEventListener('filter:rendered', (event) => {
      const t = event.target instanceof HTMLElement ? event.target : document;
      runAll(t);
    });

    const observeTargets = document.querySelectorAll(
      [
        '.main-collection-grid',
        '.facets',
        'facet-filters-form',
        'facet-inputs-component',
        'sf-recently-viewed',
        '.cart-drawer',
        // PDP product media gallery loads deferred video/3D players.
        'sf-product-media-scroll',
        '.deferred-media',
      ].join(',')
    );
    if (observeTargets.length) {
      const mo = new MutationObserver((mutations) => {
        let scheduled = false;
        for (const m of mutations) {
          if (m.addedNodes.length) {
            scheduled = true;
            break;
          }
        }
        if (scheduled) {
          // Coalesce — one pass after the current microtask batch.
          requestAnimationFrame(() => runAll(document));
        }
      });
      observeTargets.forEach((node) =>
        mo.observe(node, { childList: true, subtree: true })
      );
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
